import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import { GetUserProfileResponse, User } from '../../models/user.model';
import { Auth } from '../../services/auth';

@Component({
  selector: 'app-header',
  imports: [RouterLink],
  templateUrl: './header.html',
  styleUrl: './header.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header implements OnInit {
  protected readonly user = signal<User | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly hasError = signal(false);
  protected readonly isMenuOpen = signal(false);
  protected readonly isLogoutModalOpen = signal(false);
  protected readonly currentUrl = signal('');

  protected readonly displayName = computed(() => {
    const user = this.user();
    const fullName = [user?.name, user?.surname].filter(Boolean).join(' ').trim();
    return fullName || user?.email || 'Utente';
  });

  protected readonly userInitials = computed(() => {
    const user = this.user();
    const first = user?.name?.[0] ?? '';
    const last = user?.surname?.[0] ?? '';
    return (first + last).toUpperCase() || '?';
  });

  protected readonly isAdmin = computed(() => this.user()?.role === 'admin');

  protected readonly isAdminArea = computed(() => this.currentUrl().startsWith('/admin'));

  protected readonly isManager = computed(() => this.user()?.role === 'manager');

  protected readonly isManagerArea = computed(() => this.currentUrl().startsWith('/manager'));

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly authService = inject(Auth);

  ngOnInit(): void {
    this.currentUrl.set(this.router.url);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects);
      }
    });
    this.fetchUserProfile();
  }

  protected isOnProfilePage(): boolean {
    return this.currentUrl().startsWith('/profile');
  }

  @HostListener('document:click', ['$event'])
  protected closeMenuOnOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (this.isMenuOpen() && !target?.closest('.user-cluster')) {
      this.isMenuOpen.set(false);
    }
  }

  protected toggleMenu(event: MouseEvent): void {
    event.stopPropagation();

    if (!this.authService.isAuthenticated()) {
      this.isMenuOpen.set(false);
      this.router.navigateByUrl('/primo-login');
      return;
    }

    this.isMenuOpen.update((isOpen) => !isOpen);
  }

  protected navigateToProfile(): void {
    if (!this.redirectToLoginWhenUnauthenticated()) {
      return;
    }
    this.isMenuOpen.set(false);
    this.router.navigateByUrl('/profile');
  }

  protected navigateToProjects(): void {
    if (!this.redirectToLoginWhenUnauthenticated()) {
      return;
    }
    this.isMenuOpen.set(false);
    this.router.navigateByUrl('/dashboard');
  }

  protected navigateToAdmin(): void {
    if (!this.redirectToLoginWhenUnauthenticated()) {
      return;
    }
    this.isMenuOpen.set(false);
    this.router.navigateByUrl('/admin');
  }

  protected navigateToManager(): void {
    if (!this.redirectToLoginWhenUnauthenticated()) {
      return;
    }
    this.isMenuOpen.set(false);
    this.router.navigateByUrl('/manager');
  }

  protected logout(): void {
    this.isMenuOpen.set(false);
    this.isLogoutModalOpen.set(true);
  }

  protected cancelLogout(): void {
    this.isLogoutModalOpen.set(false);
  }

  protected confirmLogout(): void {
    this.authService.removeToken();
    this.isLogoutModalOpen.set(false);
    this.router.navigateByUrl('/login');
  }

  private fetchUserProfile(): void {
    this.hasError.set(false);

    if (!this.authService.isAuthenticated()) {
      this.hasError.set(true);
      this.router.navigateByUrl('/primo-login');
      return;
    }

    this.isLoading.set(true);

    this.http.get<GetUserProfileResponse>('/api/user/profile').subscribe({
      next: (res) => {
        this.user.set(res.user ?? null);
        this.isLoading.set(false);
      },
      error: () => {
        this.user.set(null);
        this.hasError.set(true);
        this.isLoading.set(false);
        this.authService.removeToken();
        this.router.navigateByUrl('/primo-login');
      },
    });
  }

  private redirectToLoginWhenUnauthenticated(): boolean {
    if (this.authService.isAuthenticated()) {
      return true;
    }
    this.isMenuOpen.set(false);
    this.router.navigateByUrl('/primo-login');
    return false;
  }
}
